import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../utils/api";

interface ImpersonationState {
  isImpersonating: boolean;
  isLoading: boolean;
  error: string | null;
  targetUserId: number | null;
}

const initialState: ImpersonationState = {
  isImpersonating: false,
  isLoading: false,
  error: null,
  targetUserId: null,
};

export const impersonateUser = createAsyncThunk<
  { targetUserId: number },
  number,
  { rejectValue: string }
>("impersonation/impersonateUser", async (targetUserId, { rejectWithValue }) => {
  try {
    const res = await api.post(`/login-as/impersonation/impersonate`, { targetUserId }, { withCredentials: true });
    return { targetUserId };
  } catch (err: any) {
    const msg = err.response?.data?.message || "Failed to impersonate user";
    return rejectWithValue(msg);
  }
});

export const stopImpersonation = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("impersonation/stopImpersonation", async (_, { rejectWithValue }) => {
  try {
    await api.post(`/login-as/impersonation/stop-impersonate`, {}, { withCredentials: true });
  } catch (err: any) {
    const msg = err.response?.data?.message || "Failed to stop impersonation";
    return rejectWithValue(msg);
  }
});

export const fetchImpersonationStatus = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>(
  "impersonation/fetchImpersonationStatus",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/login-as/info`, {
        withCredentials: true,
      });
      return res.data.isImpersonating;
    } catch (err: any) {
      const msg = err.response?.data?.message || "Failed to fetch impersonation status";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const impersonationSlice = createSlice({
  name: "impersonation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(impersonateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(impersonateUser.fulfilled, (state, action: PayloadAction<{ targetUserId: number }>) => {
        state.isLoading = false;
        state.isImpersonating = true;
        state.targetUserId = action.payload.targetUserId;
      })
      .addCase(impersonateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Impersonation failed";
      })

      .addCase(stopImpersonation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(stopImpersonation.fulfilled, (state) => {
        state.isLoading = false;
        state.isImpersonating = false;
        state.targetUserId = null;
      })
      .addCase(stopImpersonation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to stop impersonation";
      })

      .addCase(fetchImpersonationStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchImpersonationStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isImpersonating = action.payload;
        state.targetUserId = null;
      })
      .addCase(fetchImpersonationStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default impersonationSlice.reducer;
